import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Mail } from 'lucide-react';
import './NewsletterSignupModal.css';
import { Camp } from '../utils/types';
import { useNewsletterSignup } from '../api/newsletter';

interface NewsletterSignupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSignupAndBook: (email: string) => void;
  onSkipAndBook: () => void;
  camp: Camp;
}

const NewsletterSignupModal: React.FC<NewsletterSignupModalProps> = ({
  isOpen,
  onClose,
  onSignupAndBook,
  onSkipAndBook,
  camp
}) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const newsletterSignupMutation = useNewsletterSignup();

  if (!isOpen) return null;

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSignup = async () => {
    setError('');

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      await newsletterSignupMutation.mutateAsync({
        email: email.trim(),
        zip_code: camp.zip_code || undefined,
      });

      onSignupAndBook(email);
    } catch (err) {
      setError('Failed to sign up for newsletter. Please try again later.');
    }
  };

  const handleSkip = () => {
    onSkipAndBook();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !newsletterSignupMutation.isPending) {
      handleSignup();
    }
  };

  const modalContent = (
    <div className="modal-overlay newsletter-modal-overlay" onClick={onClose}>
      <div className="newsletter-modal-content modal-content" onClick={(e) => e.stopPropagation()}>
        <button
          className="newsletter-modal-close-btn modal-close-btn"
          onClick={onClose}
          aria-label="Close"
          disabled={newsletterSignupMutation.isPending}
        >
          <X />
        </button>

        <div className="newsletter-signup-aika-icon">
          <img src="/images/aika-hedgehog.svg" alt="Aika Hedgehog" />
        </div>

        <p className="newsletter-signup-modal-description">
          Before you go to book your camp - want to hear about new camps and discounts?
        </p>

        {error && (
          <div className="message message--error">
            {error}
          </div>
        )}

        <div className="newsletter-form">
          <div className="form-group">
            <input
              id="newsletter-email"
              type="email"
              className="form-input"
              placeholder="Your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={newsletterSignupMutation.isPending}
            />
          </div>

          <div className="newsletter-actions">
            <button
              className="btn btn--primary btn--md newsletter-signup-btn"
              onClick={handleSignup}
              disabled={newsletterSignupMutation.isPending}
            >
              {newsletterSignupMutation.isPending ? 'Signing up...' : 'Sign up for our newsletter & continue'}
            </button>
            <button
              className="btn btn--link btn--sm"
              onClick={handleSkip}
              disabled={newsletterSignupMutation.isPending}
            >
              No thanks, continue without signing up
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default NewsletterSignupModal;
